/**
 * Custom Element Auto-Loader
 * -------------------------
 * This module provides automatic discovery and lazy/eager loading of custom elements
 * in both regular DOM and Shadow DOM contexts. It monitors DOM mutations and handles
 * dynamic element registration as elements enter the viewport.
 *
 * Key Features:
 * - Automatic discovery of undefined custom elements
 * - Support for both lazy (default) and eager loading strategies
 * - Shadow DOM support with recursive element discovery
 * - Mutation observation for dynamically added elements
 * - Configurable viewport-based lazy loading
 * - Build-time cache busting via query parameter versioning
 *
 * Configuration:
 * 1. Loading Strategy:
 *    Configure via drupalSettings:
 *    window.drupalSettings.outlineLoader = { type: 'lazy' | 'eager' };
 *
 * 2. Custom Element Prefixes:
 *    By default, this loader only processes elements starting with 'outline-'.
 *    To handle multiple prefixes, update ALLOWED_PREFIXES:
 *    ```
 *    const ALLOWED_PREFIXES = ['outline']; // Single prefix
 *    // or ['outline', 'sample', 'your-prefix'] for multiple prefixes
 *
 *    const filteredElements = undefinedElements.filter(element => {
 *      const tagName = element.tagName.toLowerCase();
 *      return ALLOWED_PREFIXES.some(prefix => tagName.startsWith(`${prefix}-`));
 *    });
 *    ```
 *
 * Debugging:
 * To enable detailed logging, set DEBUG = true at the top of this file.
 * Debug logs will show:
 * - 🚀 Loader initialization
 * - 🔍 Element discovery process
 * - 📝 Element registration
 * - 🔄 Mutation observations
 * - ❌ Error messages
 *
 * For advanced troubleshooting of dynamic import failures, see the
 * Dynamic Import Diagnostics Tool at ./troubleshooting.mdx which provides
 * comprehensive analysis and automated testing scripts.
 *
 * Cache Busting:
 * Component URLs automatically include a build timestamp query parameter (?v=timestamp)
 * to ensure fresh components are loaded after deployments. The timestamp is injected
 * during the build process and remains constant until the next build, preventing
 * unnecessary cache invalidation during development.
 *
 * Cache busting is automatically disabled for TypeScript files (.ts) to ensure
 * compatibility with Vite's dev server, which doesn't handle query parameters
 * well on TypeScript imports.
 * @see vite.config.js
 *
 * Expected File Structure:
 * Custom elements should follow the pattern:
 * /components/element-name/element-name.js
 */

import { getBasePath } from '../utilities/base-path.js';

interface DrupalSettings {
  outlineLoader?: { type: string };
}
declare global {
  interface Window {
    drupalSettings?: DrupalSettings;
  }
  // Build-time injected constants (may not be defined in all environments)
  const __OUTLINE_BUILD_TIMESTAMP__: number | undefined;
}

const DEBUG = false;

// Build timestamp for cache busting - injected during build process
// Fallback to 0 if not defined (defensive programming)
const BUILD_TIMESTAMP =
  typeof __OUTLINE_BUILD_TIMESTAMP__ !== 'undefined'
    ? __OUTLINE_BUILD_TIMESTAMP__
    : 0;

// Update this to load project specific prefixes
// Example: ['outline'] or ['outline', 'sample', 'your-prefix'] for multiple prefixes
const ALLOWED_PREFIXES = ['outline', 'labcoat'];

/**
 * Debug logging utility function that only logs when DEBUG flag is true.
 * @param args - Variable number of arguments to log
 * @private
 */
function log(...args: unknown[]): void {
  if (DEBUG) {
    console.log('[outline-loader]', ...args);
  }
}

/**
 * MutationObserver callback that monitors DOM changes and discovers new custom elements.
 * Processes both regular DOM nodes and Shadow DOM roots for undefined custom elements.
 * @private
 */
const mutationObserver = new MutationObserver(mutations => {
  log('🔄 Mutation observer triggered', { mutations });
  for (const { addedNodes } of mutations) {
    log('Processing mutation with addedNodes:', addedNodes);
    for (const node of addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const elementNode = node as Element;
        log('Processing element node:', elementNode.tagName);
        discoverElements(elementNode);
        if (elementNode.shadowRoot !== null) {
          log('Found shadow root in:', elementNode.tagName);
          discoverElements(elementNode.shadowRoot);
        }
      } else {
        log('Skipping non-element node:', node.nodeType);
      }
    }
  }
});

/**
 * Sets up an IntersectionObserver to monitor when an element enters the viewport.
 * Used for lazy loading of custom elements.
 *
 * @param element - The DOM element to observe
 * @param callback - Callback function that receives a boolean indicating if element is in view
 * @private
 */
function observeElementInViewport(
  element: Element,
  callback: (isInView: boolean) => void
): void {
  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          callback(true);
          observer.unobserve(entry.target); // Unobserve the element after it is in view
        }
      });
    },
    // look ahead 500px above and below the viewport
    { root: null, rootMargin: '500px 0px 500px 0px', threshold: 0 }
  );

  observer.observe(element);
}

/**
 * Discovers and processes undefined custom elements within a given root element or shadow root.
 * Handles both eager and lazy loading strategies based on configuration.
 *
 * @param root - The root element or shadow root to search within
 * @returns Promise that resolves when all discovered elements are processed
 * @public
 */
export async function discoverElements(root: Element | ShadowRoot) {
  log(
    '🔍 Discovering elements in:',
    root instanceof Element ? root.tagName : 'ShadowRoot'
  );

  log('Checking root element and querying for undefined child elements');
  const undefinedElements = [
    ...(root instanceof Element && !root.matches(':defined') ? [root] : []),
    ...root.querySelectorAll(':not(:defined)'),
  ];
  log('Query results:', {
    rootIsElement: root instanceof Element,
    rootIsUndefined: root instanceof Element && !root.matches(':defined'),
    queriedElements: root.querySelectorAll(':not(:defined)').length,
  });
  log('Found undefined elements:', undefinedElements.length);

  if (undefinedElements.length === 0) {
    log('No undefined elements found, returning');
    return;
  }

  const filteredElements = undefinedElements.filter(element => {
    const tagName = element.tagName.toLowerCase();
    return ALLOWED_PREFIXES.some(prefix => tagName.startsWith(`${prefix}-`));
  });
  log(
    'Filtered custom elements:',
    filteredElements.map(el => el.tagName)
  );

  // Create a Map of unique tag names to their corresponding elements
  const uniqueElementsMap = new Map(
    filteredElements.map(element => [element.tagName.toLowerCase(), element])
  );
  log('Unique elements map:', Array.from(uniqueElementsMap.keys()));

  // if eager - register all elements
  // if lazy (default) - register elements when they are in viewport
  const loaderType = window?.drupalSettings?.outlineLoader?.type ?? 'lazy';
  log('Loader type:', loaderType);

  if (loaderType === 'lazy') {
    log('Using lazy loading strategy');
    filteredElements.forEach(element => {
      log('Setting up intersection observer for:', element.tagName);
      observeElementInViewport(element, async isInView => {
        log('Intersection callback triggered:', {
          element: element.tagName,
          isInView,
        });
        if (isInView) {
          await register(element);
        }
      });
    });
  } else {
    log('Using eager loading strategy');
    await Promise.allSettled(
      Array.from(uniqueElementsMap.values()).map(element => register(element))
    );
  }
}

/**
 * Initializes the custom elements discovery system for a given target.
 * Sets up mutation observers and performs initial element discovery.
 *
 * @param target - The target element or shadow root to initialize discovery on
 * @private
 */
function initializeCustomElementsDiscovery(target: Element | ShadowRoot): void {
  log(
    '🎯 Initializing discovery for:',
    target instanceof Element ? target.tagName : 'ShadowRoot'
  );

  // Discover any existing custom elements
  discoverElements(target);

  // Setup mutation observer to catch future DOM changes
  log(
    'Setting up mutation observer for:',
    target instanceof Element ? target.tagName : 'ShadowRoot'
  );
  mutationObserver.observe(target, {
    subtree: true,
    childList: true,
  });
}

/**
 * Dynamically imports and registers a custom element module.
 * Handles both development and production paths, and sets up shadow root observation
 * if the element creates a shadow root.
 *
 * @param element - The element to register
 * @returns Promise that resolves when the element is registered
 * @private
 */
function register(element: Element): Promise<void> {
  const tagName = element.tagName.toLowerCase();
  log('📝 Registering element:', tagName);

  // If the element is already defined, there's nothing more to do.
  if (customElements.get(tagName)) {
    log('Element already registered:', tagName);
    return Promise.resolve();
  }

  // Support lazy loading in development mode
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const extension = (window as any)?.outline_loader_component_extension ?? 'js';

  // Add build timestamp for cache busting with defensive checks
  // Skip cache busting for TypeScript files in dev mode (Vite doesn't like query params on .ts files)
  const shouldCacheBust =
    typeof BUILD_TIMESTAMP === 'number' &&
    BUILD_TIMESTAMP > 0 &&
    extension !== 'ts';

  const cacheBuster = shouldCacheBust ? `-${BUILD_TIMESTAMP}` : '';
  const path = getBasePath(
    `components/${tagName}/${tagName}${cacheBuster}.${extension}`
  );
  log('Loading from path:', path);

  // Register it
  return new Promise<void>((resolve, reject) => {
    import(/* @vite-ignore */ path)
      .then(() => {
        log('Successfully imported:', tagName);
        // if element has shadowroot - discover and observe it to find undefined elements
        if (element.shadowRoot) {
          log('Found shadow root, initializing discovery');
          // Find additional undefined components that might have been cloned into the shadowRoot of the element
          initializeCustomElementsDiscovery(element.shadowRoot);
        }
        resolve();
      })
      .catch(error => {
        log('❌ Error loading element:', tagName, error);
        console.error(
          `Error occurred during import of <${tagName}> from ${path}:`,
          error
        );
        reject(new Error(`Unable to autoload <${tagName}> from ${path}`));
      });
  });
}

// Initial discovery and setup
log('🚀 Starting initial discovery');
initializeCustomElementsDiscovery(document.body);
